import * as React from 'react'

import { cn } from '@/lib/utils'

import { Label } from '@/components/ui/label'

export type FormFieldProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  label: React.ReactNode
  htmlFor?: string
  /** Optional muted line under the control (hints, constraints). */
  description?: React.ReactNode
  /** Same as **`description`**; prefer **`description`** for new code. */
  hint?: React.ReactNode
  error?: React.ReactNode
  children: React.ReactNode
}

/**
 * Canonical vertical stack **`Label` → control → hint/error** with **`gap-2`**.
 * Prefer this over repetitive **`space-y-2` + `Label`** stacks per DESIGN_SYSTEM remediation Pass 6.
 */
function FormField({
  label,
  htmlFor,
  description,
  hint,
  error,
  className,
  children,
  ...props
}: FormFieldProps) {
  const generatedId = React.useId()
  const support = description ?? hint
  const soleChild =
    React.Children.count(children) === 1 && React.isValidElement(children)
      ? (children as React.ReactElement<Record<string, unknown>>)
      : null
  const childId = soleChild?.props.id as string | undefined
  const controlId = htmlFor ?? childId ?? generatedId
  const supportId = support ? `${controlId}-description` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  const describedBy =
    [soleChild?.props['aria-describedby'], supportId, errorId].filter(Boolean).join(' ') ||
    undefined
  const control = soleChild
    ? React.cloneElement(soleChild, {
        id: childId ?? controlId,
        'aria-describedby': describedBy,
        ...(error ? { 'aria-invalid': true } : {}),
      })
    : children

  return (
    <div data-slot="form-field" className={cn('flex flex-col gap-2', className)} {...props}>
      <Label htmlFor={controlId} className="text-sm font-medium">
        {label}
      </Label>
      {control}
      {support ? (
        typeof support === 'string' ? (
          <p id={supportId} className="text-xs text-muted-foreground">
            {support}
          </p>
        ) : (
          <div id={supportId} className="text-xs text-muted-foreground">
            {support}
          </div>
        )
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export { FormField }
